import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { Article, FilterType } from '../types';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { slugify } from '../utils/slug';
import { dispatchChapterPushNotification } from '../services/webPushService';

export interface SchedulePromptResult {
  success: boolean;
  article?: Article;
  scheduleExplanation?: string;
  isScheduled?: boolean;
  scheduledAt?: string | null;
  error?: string;
}

interface ArticleContextType {
  articles: Article[]; // Artigos públicos publicados
  allArticles: Article[]; // Todos os artigos (incluindo agendados, para o painel admin)
  scheduledArticles: Article[]; // Apenas os artigos agendados para o futuro
  publishedArticles: Article[]; // Apenas os artigos publicados
  addArticle: (article: Omit<Article, 'id' | 'date'> & { date?: string }) => Promise<string | undefined>;
  scheduleArticleWithPrompt: (prompt: string) => Promise<SchedulePromptResult>;
  publishNow: (id: string) => Promise<void>;
  updateArticle: (id: string, updates: Partial<Article>) => Promise<void>;
  deleteArticle: (id: string) => Promise<boolean>;
  syncPendingArticles: () => Promise<number>;
  deleteAllArticles: () => Promise<boolean>;
  isAdmin: boolean;
  login: (email: string, password: string) => boolean;
  logout: () => void;
}

const ArticleContext = createContext<ArticleContextType | undefined>(undefined);

export const ArticleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [allRawArticles, setAllRawArticles] = useState<Article[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('literafan_articles_cache');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
          }
        }
      } catch {}
    }
    return [];
  });
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('literafan_admin_auth') === 'true';
    }
    return false;
  });

  // Fast first-visit seed while Firestore establishes connection
  useEffect(() => {
    if (allRawArticles.length === 0 && typeof window !== 'undefined') {
      fetch('/pending_articles.json')
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            setAllRawArticles(prev => {
              if (prev.length === 0) {
                return data.map((item: any, idx: number) => ({
                  id: 'seed_' + idx,
                  title: item.title,
                  slug: slugify(item.title),
                  excerpt: item.excerpt || '',
                  category: Array.isArray(item.category) ? item.category[0] : (item.category || 'Geral'),
                  author: item.author || 'Sebastião Victor Diniz',
                  date: 'Jan 2026',
                  imageUrl: item.imageUrl,
                  tags: item.tags || [],
                  status: 'published',
                  content: item.content || ''
                }));
              }
              return prev;
            });
          }
        })
        .catch(() => {});
    }
  }, []);

  // Helper para verificar se um artigo está programado para o futuro
  const isArticleFutureScheduled = (data: any): boolean => {
    if (data.status === 'scheduled' || data.isScheduled === true) {
      if (data.scheduledAt) {
        const scheduledTime = new Date(data.scheduledAt).getTime();
        return scheduledTime > Date.now();
      }
      return true; // Se marcado como agendado mas sem data, trata como agendado
    }
    return false;
  };

  // Carregar artigos do Firebase em tempo real
  useEffect(() => {
    const q = query(collection(db, 'articles'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
      const now = Date.now();
      const docsToAutoPublish: { id: string; title: string }[] = [];

      const fetchedArticles = snapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data();
        let publishDate = data.date;
        if (!publishDate && data.createdAt) {
           publishDate = typeof data.createdAt.toDate === 'function' 
              ? data.createdAt.toDate().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
              : new Date(data.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
        } else if (!publishDate) {
           publishDate = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
        }

        const scheduledAtStr = data.scheduledAt || null;
        let isScheduled = data.isScheduled || false;
        let status = data.status || (isScheduled ? 'scheduled' : 'published');

        // Verificação de auto-publicação automática caso a data/hora agendada tenha chegado
        if (status === 'scheduled' && scheduledAtStr) {
          const scheduledTime = new Date(scheduledAtStr).getTime();
          if (scheduledTime <= now) {
            // A data/hora estipulada chegou! Deve ser publicado automaticamente.
            status = 'published';
            isScheduled = false;
            docsToAutoPublish.push({ id: docSnapshot.id, title: data.title });
          }
        }
        
        const rawSlug = data.slug || (data.title ? slugify(data.title) : docSnapshot.id);
        
        return {
          id: docSnapshot.id,
          title: data.title,
          slug: rawSlug,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          author: data.author,
          date: publishDate,
          imageUrl: data.imageUrl,
          tags: data.tags || [],
          audioUrl: data.audioUrl,
          authorImage: data.authorImage,
          status: status,
          scheduledAt: scheduledAtStr,
          isScheduled: isScheduled,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        } as Article;
      });

      // Centralized sorting by date descending
      fetchedArticles.sort((a, b) => {
        const parsePtBrDate = (dateStr: string) => {
          if (!dateStr) return 0;
          const months: Record<string, number> = {
            'jan': 0, 'fev': 1, 'mar': 2, 'abr': 3, 'mai': 4, 'jun': 5,
            'jul': 6, 'ago': 7, 'set': 8, 'out': 9, 'nov': 10, 'dez': 11
          };
          const parts = dateStr.toLowerCase().replace(/de /g, '').split(' ').filter(Boolean);
          if (parts.length >= 3) {
            const day = parseInt(parts[0], 10);
            const monthStr = parts[1];
            let month = months[monthStr];
            if (month === undefined) {
               for (const key of Object.keys(months)) {
                 if (monthStr.startsWith(key)) { month = months[key]; break; }
               }
            }
            const year = parseInt(parts[2], 10);
            if (!isNaN(day) && month !== undefined && !isNaN(year)) {
              return new Date(year, month, day).getTime();
            }
          }
          return 0;
        };
        return parsePtBrDate(b.date) - parsePtBrDate(a.date);
      });

      setAllRawArticles(fetchedArticles);
      if (typeof window !== 'undefined') {
        try {
          const cacheData = fetchedArticles.slice(0, 40).map(a => ({
            id: a.id,
            title: a.title,
            slug: a.slug,
            excerpt: a.excerpt,
            category: a.category,
            author: a.author,
            date: a.date,
            imageUrl: a.imageUrl,
            tags: a.tags,
            audioUrl: a.audioUrl,
            authorImage: a.authorImage,
            status: a.status
          }));
          localStorage.setItem('literafan_articles_cache', JSON.stringify(cacheData));
        } catch {}
      }

      // Auto-publish docs in Firestore that reached their scheduled time
      if (docsToAutoPublish.length > 0) {
        console.log(`[Literafan Scheduler] Auto-publicando ${docsToAutoPublish.length} artigo(s) cuja data/hora foi atingida...`);
        for (const docItem of docsToAutoPublish) {
          try {
            await updateDoc(doc(db, 'articles', docItem.id), {
              status: 'published',
              isScheduled: false,
              updatedAt: serverTimestamp()
            });
            console.log(`[Literafan Scheduler] Artigo publicado com sucesso: "${docItem.title}"`);
          } catch (err) {
            console.error(`Erro ao atualizar artigo auto-publicado ${docItem.id}:`, err);
          }
        }
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'articles');
    });

    const storedAuth = localStorage.getItem('literafan_admin_auth');
    if (storedAuth === 'true') {
      setIsAdmin(true);
    }

    return () => unsubscribe();
  }, []);

  // Intervalo de verificação contínua de agendamento (a cada 10 segundos)
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setAllRawArticles(currentArticles => {
        let changed = false;
        const updated = currentArticles.map(art => {
          if (art.status === 'scheduled' && art.scheduledAt) {
            const schedTime = new Date(art.scheduledAt).getTime();
            if (schedTime <= now) {
              changed = true;
              // Trigger async firestore update
              updateDoc(doc(db, 'articles', art.id), {
                status: 'published',
                isScheduled: false,
                updatedAt: serverTimestamp()
              }).catch(e => console.error("Erro ao atualizar artigo agendado:", e));

              return {
                ...art,
                status: 'published' as const,
                isScheduled: false
              };
            }
          }
          return art;
        });
        return changed ? updated : currentArticles;
      });
    }, 10000);

    return () => clearInterval(timer);
  }, []);

  // Artigos visíveis publicamente (apenas os que já estão publicados)
  const articles = allRawArticles.filter(art => {
    if (art.status === 'scheduled' || art.isScheduled) {
      if (art.scheduledAt) {
        return new Date(art.scheduledAt).getTime() <= Date.now();
      }
      return false;
    }
    return true;
  });

  // Artigos agendados pendentes (para o painel de administração)
  const scheduledArticles = allRawArticles.filter(art => {
    if (art.status === 'scheduled' || art.isScheduled) {
      if (art.scheduledAt) {
        return new Date(art.scheduledAt).getTime() > Date.now();
      }
      return true;
    }
    return false;
  });

  // Artigos publicados
  const publishedArticles = allRawArticles.filter(art => {
    if (art.status === 'scheduled' || art.isScheduled) {
      if (art.scheduledAt) {
        return new Date(art.scheduledAt).getTime() <= Date.now();
      }
      return false;
    }
    return true;
  });

  // Adicionar novo artigo diretamente
  const addArticle = async (newArticleData: Omit<Article, 'id' | 'date'> & { date?: string }): Promise<string | undefined> => {
    try {
      const now = new Date();
      const isFuture = newArticleData.scheduledAt 
        ? new Date(newArticleData.scheduledAt).getTime() > now.getTime() 
        : false;

      const dateStr = newArticleData.date || now.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
      const articleSlug = newArticleData.slug || slugify(newArticleData.title);
      
      const docRef = await addDoc(collection(db, 'articles'), {
        ...newArticleData,
        slug: articleSlug,
        date: dateStr,
        status: isFuture ? 'scheduled' : (newArticleData.status || 'published'),
        isScheduled: isFuture || (newArticleData.isScheduled || false),
        scheduledAt: newArticleData.scheduledAt || null,
        authorId: 'admin_user_id',
        createdAt: serverTimestamp()
      });

      // Se publicado imediatamente, notifica os leitores via Web Push e E-mail da Newsletter
      if (!isFuture) {
        dispatchChapterPushNotification({
          title: newArticleData.title,
          chapterTitle: newArticleData.title,
          slug: articleSlug,
          excerpt: newArticleData.excerpt,
          imageUrl: newArticleData.imageUrl,
          category: Array.isArray(newArticleData.category) ? newArticleData.category[0] : newArticleData.category,
          author: newArticleData.author,
          articleId: docRef.id,
          sendNewsletter: true,
          sendPush: true,
        }).catch(err => console.warn('[Publication] Falha ao enviar notificação de artigo:', err));
      }

      return docRef.id;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'articles');
    }
  };

  // Programar artigo através de Prompt em Linguagem Natural com IA Gemini
  const scheduleArticleWithPrompt = async (prompt: string): Promise<SchedulePromptResult> => {
    try {
      const now = new Date();
      const response = await fetch('/api/articles/parse-and-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt,
          clientCurrentTime: now.toISOString(),
          clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'
        })
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || `Erro no servidor: status ${response.status}`);
      }

      const parsed = await response.json();

      // Salvar no Firebase Firestore
      const isFuture = parsed.isScheduled && parsed.scheduledAt 
        ? new Date(parsed.scheduledAt).getTime() > now.getTime()
        : false;

      const dateDisplay = parsed.formattedDate || (parsed.scheduledAt 
        ? new Date(parsed.scheduledAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
        : now.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }));

      const articleSlug = slugify(parsed.title);

      const articlePayload = {
        title: parsed.title,
        slug: articleSlug,
        excerpt: parsed.excerpt,
        content: parsed.content,
        category: parsed.category,
        tags: parsed.tags || [],
        author: parsed.author || 'Equipe Literafan',
        date: dateDisplay,
        imageUrl: parsed.imageUrl || 'https://images.unsplash.com/photo-1507842229451-79730c007137?auto=format&fit=crop&w=1200&q=80',
        metaDescription: parsed.metaDescription || parsed.excerpt,
        status: isFuture ? 'scheduled' : 'published',
        isScheduled: isFuture,
        scheduledAt: parsed.scheduledAt || null,
        authorId: 'admin_user_id',
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'articles'), articlePayload);

      // Se publicado imediatamente, notifica leitores por Web Push e Newsletter
      if (!isFuture) {
        dispatchChapterPushNotification({
          title: parsed.title,
          chapterTitle: parsed.title,
          slug: articleSlug,
          excerpt: parsed.excerpt,
          imageUrl: parsed.imageUrl,
          category: parsed.category,
          author: parsed.author,
          articleId: docRef.id,
          sendNewsletter: true,
          sendPush: true,
        }).catch(err => console.warn('[Publication] Falha ao enviar notificação de artigo por prompt:', err));
      }

      const createdArticle: Article = {
        id: docRef.id,
        ...articlePayload,
        status: isFuture ? 'scheduled' : 'published',
        isScheduled: isFuture,
        scheduledAt: parsed.scheduledAt || undefined
      };

      return {
        success: true,
        article: createdArticle,
        scheduleExplanation: parsed.scheduleExplanation || (isFuture ? `Programado para ${dateDisplay}` : 'Publicação Imediata'),
        isScheduled: isFuture,
        scheduledAt: parsed.scheduledAt
      };
    } catch (error: any) {
      console.error("Erro ao programar artigo por prompt:", error);
      return {
        success: false,
        error: error.message || "Falha ao processar o agendamento do artigo."
      };
    }
  };

  // Publicar imediatamente um artigo agendado
  const publishNow = async (id: string) => {
    try {
      const nowStr = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
      await updateDoc(doc(db, 'articles', id), {
        status: 'published',
        isScheduled: false,
        date: nowStr,
        updatedAt: serverTimestamp()
      });

      const article = allRawArticles.find(a => a.id === id);
      if (article) {
        dispatchChapterPushNotification({
          title: article.title,
          chapterTitle: article.title,
          slug: article.slug || slugify(article.title),
          excerpt: article.excerpt,
          imageUrl: article.imageUrl,
          category: Array.isArray(article.category) ? article.category[0] : article.category,
          author: article.author,
          articleId: article.id,
          sendNewsletter: true,
          sendPush: true,
        }).catch(err => console.warn('[Publication] Falha ao enviar notificação de publicação imediata:', err));
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'articles');
    }
  };

  const updateArticle = async (id: string, updates: Partial<Article>) => {
    try {
      const payload: any = { ...updates };
      if (updates.title && !updates.slug) {
        payload.slug = slugify(updates.title);
      }
      await updateDoc(doc(db, 'articles', id), {
        ...payload,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'articles');
    }
  };

  const deleteArticle = async (id: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, 'articles', id));
      return true;
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'articles');
      return false;
    }
  };

  const syncPendingArticles = async () => {
    try {
      const response = await fetch('/pending_articles.json');
      if (!response.ok) return 0;
      
      const pendingArticles = await response.json();
      let count = 0;
      
      for (const article of pendingArticles) {
        const existingArticle = allRawArticles.find(a => a.title === article.title);
        if (!existingArticle) {
          await addDoc(collection(db, 'articles'), {
            ...article,
            status: 'published',
            isScheduled: false,
            authorId: 'admin_user_id',
            createdAt: serverTimestamp()
          });
          count++;
        } else {
          await updateDoc(doc(db, 'articles', existingArticle.id), {
            ...article,
            updatedAt: serverTimestamp()
          });
          count++;
        }
      }
      return count;
    } catch (error) {
      console.error("Erro ao sincronizar artigos pendentes:", error);
      return 0;
    }
  };

  const deleteAllArticles = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'articles'));
      const deletePromises = querySnapshot.docs.map(docSnapshot => 
        deleteDoc(doc(db, 'articles', docSnapshot.id))
      );
      await Promise.all(deletePromises);
      return true;
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'articles');
      return false;
    }
  };

  const login = (email: string, password: string) => {
    const cleanEmail = email ? email.trim().toLowerCase() : '';
    const authorizedEmails = ['site.literafan@gmail.com', 'sevidiz.victordiniz@gmail.com'];
    if (authorizedEmails.includes(cleanEmail) && password === 'Administração.site.literafan.2028') {
      setIsAdmin(true);
      localStorage.setItem('literafan_admin_auth', 'true');
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAdmin(false);
    localStorage.removeItem('literafan_admin_auth');
  };

  return (
    <ArticleContext.Provider 
      value={{ 
        articles, 
        allArticles: allRawArticles,
        scheduledArticles,
        publishedArticles,
        addArticle, 
        scheduleArticleWithPrompt,
        publishNow,
        updateArticle, 
        deleteArticle,
        syncPendingArticles, 
        deleteAllArticles, 
        isAdmin, 
        login, 
        logout 
      }}
    >
      {children}
    </ArticleContext.Provider>
  );
};

export const useArticles = () => {
  const context = useContext(ArticleContext);
  if (context === undefined) {
    throw new Error('useArticles must be used within an ArticleProvider');
  }
  return context;
};

export const useArticle = useArticles;
